import { useMemo } from "react";
import { useSilver } from "@/context/SilverContext";

const W = 420;
const H = 320;
const COUNT = 240;

function seeded(seed) {
  let s = seed % 2147483647 || 1;
  return () => (s = (s * 16807) % 2147483647) / 2147483647;
}

function layout(geo) {
  const list = geo.slice(0, 10);
  const n = list.length;
  const cx = W / 2;
  const cy = H / 2;
  return list.map((g, i) => {
    if (n === 1) return { ...g, x: cx, y: cy };
    if (n <= 3) return { ...g, x: W * ((i + 1) / (n + 1)), y: cy + (i % 2 ? 40 : -40) };
    const a = i * 2.39996 - Math.PI / 2;
    const r = 44 + 92 * Math.sqrt(i / Math.max(1, n - 1));
    return { ...g, x: cx + Math.cos(a) * r * 1.25, y: cy + Math.sin(a) * r * 0.9 };
  });
}

// Abstract reach visual — districts as connected nodes, households as particles. Not a literal map.
export function ReachVisual() {
  const { audience, selectedQuantity } = useSilver();
  const nodes = useMemo(() => layout(audience.geography), [audience.geography]);
  const max = Math.max(1, ...nodes.map((n) => n.households));
  const particles = useMemo(() => {
    const total = nodes.reduce((a, n) => a + n.households, 0) || 1;
    const rand = seeded(97);
    const out = [];
    nodes.forEach((n) => {
      const nr = 8 + 20 * Math.sqrt(n.households / max);
      const count = n.households ? Math.max(6, Math.round((COUNT * n.households) / total)) : 0;
      for (let k = 0; k < count; k += 1) {
        const a = rand() * Math.PI * 2;
        const r = nr + 6 + Math.sqrt(rand()) * 34;
        out.push({ x: n.x + Math.cos(a) * r, y: n.y + Math.sin(a) * r * 0.85, order: rand() });
      }
    });
    return out.sort((a, b) => a.order - b.order);
  }, [nodes, max]);
  const coverage = audience.total ? selectedQuantity / audience.total : 0;
  const lit = Math.round(particles.length * coverage);
  const edges = nodes.slice(1).map((n, i) => {
    let best = nodes[0];
    let d = Infinity;
    nodes.slice(0, i + 1).forEach((m) => {
      const dd = Math.hypot(m.x - n.x, m.y - n.y);
      if (dd < d) { d = dd; best = m; }
    });
    return [best, n];
  });

  return (
    <figure className="relative" data-testid="reach-visual">
      <svg viewBox={`0 0 ${W} ${H}`} className="h-auto w-full" role="img" aria-label={`Reach visual: ${Math.round(coverage * 100)}% of matching households selected across ${nodes.length} districts`}>
        {edges.map(([a, b]) => (
          <line key={`${a.code}-${b.code}`} x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke="rgba(198,238,90,0.25)" strokeDasharray="2 5" />
        ))}
        {particles.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r={i < lit ? 2.1 : 1.6}
            style={{ fill: i < lit ? "#C6EE5A" : "rgba(255,255,255,0.22)", transition: `fill 500ms ease ${(i % 30) * 8}ms, r 500ms ease` }} />
        ))}
        {nodes.map((n) => {
          const r = 8 + 20 * Math.sqrt(n.households / max);
          return (
            <g key={n.code}>
              <circle cx={n.x} cy={n.y} r={r} fill="rgba(18,41,15,0.85)" stroke="#96C11F" strokeWidth="1.3" />
              <text x={n.x} y={n.y + 4} textAnchor="middle" fontSize={r > 16 ? 11 : 9} fontWeight="700" fill="#fff" className="font-ui" letterSpacing="0.6">{n.code}</text>
            </g>
          );
        })}
      </svg>
      <figcaption className="mt-2 flex items-center justify-between font-ui text-[10.5px] uppercase tracking-[0.12em] text-white/45">
        <span className="flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-[#C6EE5A]" /> Selected reach</span>
        <span className="flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-white/25" /> Available</span>
        {audience.geography.length > 10 && <span>+{audience.geography.length - 10} districts</span>}
      </figcaption>
    </figure>
  );
}
